import { skipToken } from "@reduxjs/toolkit/query";
import {
  checkFunnelUiVisible,
  checkFunnelUiWritable,
  FunnelInductionStatus,
  type FunnelId,
} from "@repo/app-types/entities/funnel";
import {
  asTeammateId,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { SimpleModalVariant } from "@repo/ui/Modal/modal";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import type { FC } from "react";
import { Navigate, useParams } from "react-router";
import {
  useFunnelQuery,
  useRetryFunnelInductionMutation,
  useVoiceAgentFunnelsQuery,
} from "../../../api/entities/funnel.js";
import { appPaths } from "../../../app/appPaths.js";
import { PageBody } from "../../../components/layout/pageLayout.js";
import { useFeatureFlagVariant } from "../../../helpers/featureFlags/hooks.js";
import { useTeammateAuthorization } from "../../../helpers/teammateAuthorizationContext.js";
import { FunnelCreateForm } from "./funnelCreateForm.js";
import { FunnelReadyView } from "./funnelReadyView.js";

/**
 * The Funnel view of the voice agent's Performance tab, behind
 * `FeatureFlagKey.VoiceAgentFunnelUi`.
 */
export const TeammateAgentFunnelTab: FC = () => {
  const { teammateAgentId: teammateAgentIdParam } = useParams();
  if (!teammateAgentIdParam) {
    throw new Error("Missing teammateAgentId param.");
  }
  const teammateAgentId = asTeammateId(teammateAgentIdParam);

  const funnelUiVariant = useFeatureFlagVariant(
    FeatureFlagKey.VoiceAgentFunnelUi,
  );
  if (!checkFunnelUiVisible(funnelUiVariant)) {
    return (
      <Navigate
        to={appPaths.teammateAgentOverview.build({ teammateAgentId })}
        replace
      />
    );
  }

  return (
    <PageBody width={PageWidth.Wide}>
      {/* Keyed so form drafts start fresh for each agent. */}
      <TeammateAgentFunnel
        key={teammateAgentId}
        teammateAgentId={teammateAgentId}
        isWritable={checkFunnelUiWritable(funnelUiVariant)}
      />
    </PageBody>
  );
};

interface TeammateAgentFunnelProps {
  teammateAgentId: TeammateId;
  /** Whether the funnel can be created and edited here. */
  isWritable: boolean;
}

const TeammateAgentFunnel: FC<TeammateAgentFunnelProps> = (props) => {
  const { teammateAgentId, isWritable } = props;

  const funnelsQuery = useVoiceAgentFunnelsQuery(teammateAgentId);
  // In v1 an agent has at most one funnel.
  const funnelId = funnelsQuery.data?.[0]?.id;
  const funnelQuery = useFunnelQuery(funnelId ?? skipToken);
  const funnel = funnelQuery.data;

  if (funnelsQuery.isLoading) {
    return <DefaultSkeleton />;
  }
  if (funnelsQuery.isError || !funnelsQuery.data) {
    return (
      <p className="text-sm text-red-700">
        We couldn&apos;t load this agent&apos;s funnel. Refresh to try again.
      </p>
    );
  }
  if (!funnelId) {
    // Read-only has no tab without a funnel.
    return isWritable ? (
      <FunnelCreateForm teammateAgentId={teammateAgentId} />
    ) : (
      <Navigate
        to={appPaths.teammateAgentOverview.build({ teammateAgentId })}
        replace
      />
    );
  }
  if (funnelQuery.isLoading) {
    return <DefaultSkeleton />;
  }
  if (funnelQuery.isError || !funnel) {
    return (
      <p className="text-sm text-red-700">
        We couldn&apos;t load this funnel. Refresh to try again.
      </p>
    );
  }

  switch (funnel.induction_status) {
    case FunnelInductionStatus.Pending:
      return <FunnelInductionPending funnelId={funnel.id} />;
    case FunnelInductionStatus.Failed:
      return (
        <FunnelInductionFailedBanner
          funnelId={funnel.id}
          inductionError={funnel.induction_error}
          isWritable={isWritable}
        />
      );
    case FunnelInductionStatus.Complete:
      return (
        <FunnelReadyView funnel={funnel} teammateAgentId={teammateAgentId} />
      );
    default:
      return assertUnreachable(funnel.induction_status);
  }
};

/** Mounted only while the funnel is pending, so its polling stops once it isn't. */
const FunnelInductionPending: FC<{ funnelId: FunnelId }> = (props) => {
  useFunnelQuery(props.funnelId, { pollingInterval: 5000 });

  return (
    <div className="flex flex-col items-center gap-3 rounded-xl bg-white px-4 py-12 text-center shadow-xs ring-1 ring-black/5">
      <Loader style={LoaderStyle.ZOOMIES} />
      <div className="text-sm font-semibold text-foreground">
        Generating the funnel
      </div>
      <p className="max-w-md text-sm text-muted-foreground">
        We&apos;re reading this agent&apos;s recent interactions to find its
        stages and moments. This takes a few minutes; this page updates on its
        own.
      </p>
    </div>
  );
};

const FunnelInductionFailedBanner: FC<{
  funnelId: FunnelId;
  inductionError: string | null;
  /** Whether to offer Retry. */
  isWritable: boolean;
}> = (props) => {
  const { funnelId, inductionError, isWritable } = props;

  const [retryInduction, { isLoading: isRetrying }] =
    useRetryFunnelInductionMutation();
  const canEdit = useTeammateAuthorization().can(
    PermissionActions.UPDATE,
    PermissionSubjects.TEAMMATES,
  );

  const handleRetry = async () => {
    try {
      await retryInduction(funnelId).unwrap();
    } catch (error) {
      console.error("Failed to retry funnel induction:", error);
      showToast({
        variant: ToastVariant.Error,
        title: "Couldn't retry",
        description: "Please try again.",
      });
    }
  };

  return (
    <InfoBanner
      variant={SimpleModalVariant.Warning}
      title="We couldn't generate the funnel"
      description={inductionError ?? "Something went wrong."}
      actions={
        isWritable ? (
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => void handleRetry()}
            isLoading={isRetrying}
            isDisabled={!canEdit}
          >
            Retry
          </Button>
        ) : null
      }
    />
  );
};
